import { useTheme } from '@material-ui/core/styles';

export interface KnowledgeBuilderColors {
  mode: 'dark' | 'light';
  monacoTheme: 'vs-dark' | 'light';
  bg: {
    root: string;
    panel: string;
    panelAlt: string;
    raised: string;
    hover: string;
    input: string;
    codeBlock: string;
  };
  border: {
    default: string;
    subtle: string;
    strong: string;
  };
  text: {
    primary: string;
    secondary: string;
    muted: string;
    /** Text color that stays legible on top of `bg.hover` (an active/selected row or tab). */
    onAccent: string;
  };
  accent: {
    blue: string;
    statusBar: string;
  };
  status: {
    errorText: string;
    errorBg: string;
    errorBorder: string;
    warningText: string;
    warningBg: string;
    warningBorder: string;
  };
  scrollbar: {
    thumb: string;
    thumbHover: string;
    track: string;
  };
  code: {
    bg: string;
    headerBg: string;
    border: string;
    text: string;
    inlineBg: string;
    inlineText: string;
    tableBorder: string;
  };
  chat: {
    userBubbleBg: string;
    userBubbleText: string;
    sendButtonBg: string;
    sendButtonText: string;
  };
}

const darkColors: KnowledgeBuilderColors = {
  mode: 'dark',
  monacoTheme: 'vs-dark',
  bg: {
    root: '#1e1e1e',
    panel: '#252526',
    panelAlt: '#2d2d2d',
    raised: '#252526',
    hover: '#37373d',
    input: '#3c3c3c',
    codeBlock: '#020617',
  },
  border: {
    default: '#2d2d2d',
    subtle: '#3a3a3a',
    strong: '#454545',
  },
  text: {
    primary: '#d4d4d4',
    secondary: '#969696',
    muted: '#858585',
    onAccent: '#ffffff',
  },
  accent: {
    blue: '#8ab4f8',
    statusBar: '#007acc',
  },
  status: {
    errorText: '#8a1f1f',
    errorBg: '#fff4f4',
    errorBorder: '#f0c7c7',
    warningText: '#fbbf24',
    warningBg: '#3a2f1b',
    warningBorder: '#5f4514',
  },
  scrollbar: {
    thumb: '#4b4b4b',
    thumbHover: '#6b6b6b',
    track: '#1e1e1e',
  },
  code: {
    bg: '#252526',
    headerBg: '#252526',
    border: '#303033',
    text: '#f9fafb',
    inlineBg: '#334155',
    inlineText: '#e2e8f0',
    tableBorder: '#334155',
  },
  chat: {
    userBubbleBg: '#2a2a2a',
    userBubbleText: '#e2e8f0',
    sendButtonBg: '#d4d4d4',
    sendButtonText: '#111827',
  },
};

const lightColors: KnowledgeBuilderColors = {
  mode: 'light',
  monacoTheme: 'light',
  bg: {
    root: '#ffffff',
    panel: '#f3f3f3',
    panelAlt: '#ececec',
    raised: '#ffffff',
    hover: '#e4e6f1',
    input: '#ffffff',
    codeBlock: '#f6f8fa',
  },
  border: {
    default: '#e0e0e0',
    subtle: '#e2e2e2',
    strong: '#cfcfcf',
  },
  text: {
    primary: '#1f1f1f',
    secondary: '#5f5f5f',
    muted: '#767676',
    onAccent: '#0b3a8c',
  },
  accent: {
    blue: '#0b5fff',
    statusBar: '#007acc',
  },
  status: {
    errorText: '#8a1f1f',
    errorBg: '#fff4f4',
    errorBorder: '#f0c7c7',
    warningText: '#92620a',
    warningBg: '#fdf3d9',
    warningBorder: '#f0dca0',
  },
  scrollbar: {
    thumb: '#c2c2c2',
    thumbHover: '#a6a6a6',
    track: '#f3f3f3',
  },
  code: {
    bg: '#f6f8fa',
    headerBg: '#eef1f4',
    border: '#e1e4e8',
    text: '#1f2328',
    inlineBg: '#eef1f4',
    inlineText: '#1f2328',
    tableBorder: '#d0d7de',
  },
  chat: {
    userBubbleBg: '#eef1f4',
    userBubbleText: '#1f2328',
    sendButtonBg: 'var(--rdap-dark-blue)',
    sendButtonText: '#ffffff',
  },
};

export function useKnowledgeBuilderColors(): KnowledgeBuilderColors {
  const theme = useTheme();
  return theme.palette.type === 'light' ? lightColors : darkColors;
}
